type OptionLike = {
	label?: string
	value?: string
}

type TagDetails = Record<string, any> | undefined

type TagBackedSource = {
	tagsID?: string
	tags?: {
		tagsID?: string
		details?: TagDetails
	}
}

type PlatformTagSource = TagBackedSource & {
	platformID?: string
	platformName?: string
}

type GcpProjectTagSource = TagBackedSource & {
	gcpProjectID?: string
	gcpProjectName?: string
}

export type ApplicationTagInheritanceSource = {
	type: 'project'
	checkboxLabel: string
	sourceID: string
	sourceName: string
	tagsID?: string
	tagDetails?: TagDetails
	isAvailable: boolean
	unavailableReason?: string
}

export const getSelectionValue = (value: unknown) => {
	if (value && typeof value === 'object' && 'value' in value) {
		return String((value as OptionLike).value ?? '')
	}

	return String(value ?? '')
}

export const getApplicationTagInheritanceTargetID = (
	formValues: Record<string, any>
) => {
	const selectedProjectID = getSelectionValue(formValues.gcpProjectID)

	return selectedProjectID ? `project:${selectedProjectID}` : ''
}

const getProjectTagsID = (project: GcpProjectTagSource) =>
	project.tagsID || project.tags?.tagsID || ''

const hasUsableTagDetails = (tagDetails: TagDetails) =>
	Boolean(tagDetails && Object.keys(tagDetails).length > 0)

export const getApplicationTagInheritanceSource = (
	formValues: Record<string, any>,
	_platforms: PlatformTagSource[] = [],
	approvedProjectsWithTags: GcpProjectTagSource[] = []
): ApplicationTagInheritanceSource | null => {
	const selectedProjectID = getSelectionValue(formValues.gcpProjectID)
	const selectedProject = approvedProjectsWithTags.find(
		(project) => project.gcpProjectID === selectedProjectID
	)

	if (!selectedProjectID || !selectedProject) {
		return null
	}

	const tagsID = getProjectTagsID(selectedProject)
	const tagDetails = selectedProject.tags?.details
	const isAvailable = Boolean(tagsID && hasUsableTagDetails(tagDetails))

	return {
		type: 'project',
		checkboxLabel: 'Use GCP Project Tags',
		sourceID: selectedProjectID,
		sourceName: selectedProject.gcpProjectName || selectedProjectID,
		tagsID,
		tagDetails,
		isAvailable,
		unavailableReason: isAvailable
			? undefined
			: 'Selected GCP Project does not have approved resource tags to inherit.'
	}
}

export const getInheritedApplicationTagValues = (
	tagDetails: TagDetails,
	source: { tagsID?: string; projectName?: string } = {}
) => ({
	tagsID: source.tagsID || '',
	projectName: source.projectName || '',
	group: tagDetails?.group || '',
	fundingSourceType: tagDetails?.fundingSourceType || '',
	dataClassification: tagDetails?.dataClassification || '',
	WBSCode: tagDetails?.WBSCode || '',
	costCenter: tagDetails?.costCenter || ''
})

export const getEmptyApplicationTagValues = () =>
	getInheritedApplicationTagValues(undefined)

/**
 * A cluster entered its own funding when it has a tag other than the project tag and it
 * was not an inherit. Older clusters have no fundingChoice: inheritGcpTagsBool false plus
 * a different tagsID is the same case. Missing or inherited clusters use the project tag.
 */
export const clusterEnteredOwnFunding = (
	cluster: { tagsID?: string; details?: Record<string, any> } | undefined,
	projectTagsID = ''
) => {
	if (!cluster?.tagsID) {
		return false
	}

	const fundingChoice = cluster.details?.fundingChoice
	if (fundingChoice === 'own') {
		return true
	}
	if (fundingChoice === 'inherit') {
		return false
	}

	return (
		cluster.details?.inheritGcpTagsBool === false &&
		cluster.tagsID !== projectTagsID
	)
}
